export type FpsLtmId =
  | 'blitz'
  | 'zero_build'
  | 'explosives'
  | 'super_siphon'
  | 'creative'
  | 'solo'
  | 'duo'
  | 'squad'
  | 'fifty_v_fifty'
  | 'zone_wars'
  | 'tilted_only'
  | 'sniper_only';

export type FpsLtmInfo = {
  id: FpsLtmId;
  name: string;
  description: string;
  color: string;
};

const LTM_BY_ID: Record<FpsLtmId, FpsLtmInfo> = {
  blitz: {
    id: 'blitz',
    name: 'Blitz',
    description: 'Storm shrinks 1.8× faster',
    color: '#ff6b35',
  },
  zero_build: {
    id: 'zero_build',
    name: 'Zero Build',
    description: 'Building disabled',
    color: '#7a8aa8',
  },
  explosives: {
    id: 'explosives',
    name: 'Explosives',
    description: 'Grenades +30% radius · +50% damage',
    color: '#ff4444',
  },
  super_siphon: {
    id: 'super_siphon',
    name: 'Super Siphon',
    description: 'Eliminations grant +40 HP/shield',
    color: '#44cc88',
  },
  creative: {
    id: 'creative',
    name: 'Creative',
    description: '999 mats · no storm dmg · fast build',
    color: '#a855f7',
  },
  solo: {
    id: 'solo',
    name: 'Solo Showdown',
    description: 'All 100 players solo – last one standing',
    color: '#1e90ff',
  },
  duo: {
    id: 'duo',
    name: 'Duos',
    description: '50 duo teams – revive your partner',
    color: '#22ccaa',
  },
  squad: {
    id: 'squad',
    name: 'Squads',
    description: '25 squads of 4 – team eliminations count double',
    color: '#ffd700',
  },
  fifty_v_fifty: {
    id: 'fifty_v_fifty',
    name: '50v50',
    description: 'Two mega-teams of 50 fight for dominance',
    color: '#ff6b35',
  },
  zone_wars: {
    id: 'zone_wars',
    name: 'Zone Wars',
    description: 'Non-stop storm – fast rotations · high pressure',
    color: '#cc3355',
  },
  tilted_only: {
    id: 'tilted_only',
    name: 'Tilted-Only',
    description: 'All players drop Tilted Towers hot zone only',
    color: '#ff4444',
  },
  sniper_only: {
    id: 'sniper_only',
    name: 'Sniper Shootout',
    description: 'Only sniper rifles spawn in the loot pool',
    color: '#9966ff',
  },
};

export const ALL_LTMS: readonly FpsLtmInfo[] = [
  LTM_BY_ID.blitz,
  LTM_BY_ID.zero_build,
  LTM_BY_ID.explosives,
  LTM_BY_ID.super_siphon,
  LTM_BY_ID.creative,
  LTM_BY_ID.solo,
  LTM_BY_ID.duo,
  LTM_BY_ID.squad,
  LTM_BY_ID.fifty_v_fifty,
  LTM_BY_ID.zone_wars,
  LTM_BY_ID.tilted_only,
  LTM_BY_ID.sniper_only,
];

const LTM_OVERRIDE_KEY = 'stormsurge-ltm-override';

export function getLtmById(id: FpsLtmId): FpsLtmInfo {
  return LTM_BY_ID[id];
}

export function getLtmOverride(): FpsLtmId | null {
  try {
    const raw = localStorage.getItem(LTM_OVERRIDE_KEY);
    if (!raw) return null;
    if (raw in LTM_BY_ID) return raw as FpsLtmId;
    return null;
  } catch {
    return null;
  }
}

export function setLtmOverride(id: FpsLtmId | null): void {
  try {
    if (id === null) {
      localStorage.removeItem(LTM_OVERRIDE_KEY);
    } else {
      localStorage.setItem(LTM_OVERRIDE_KEY, id);
    }
  } catch {
    /* ignore quota / private mode */
  }
}

/** Active LTM — manual override or daily rotation. */
export function getActiveLtm(now: Date = new Date()): FpsLtmInfo {
  const override = getLtmOverride();
  if (override) return getLtmById(override);
  return getDailyLtm(now);
}

/** Daily rotating Limited Time Mode keyed to day-of-week (local time). */
export function getDailyLtm(now: Date = new Date()): FpsLtmInfo {
  const dow = now.getDay();
  if (dow === 1 || dow === 3) return LTM_BY_ID.blitz;
  if (dow === 2 || dow === 4) return LTM_BY_ID.zero_build;
  if (dow === 5) return LTM_BY_ID.explosives;
  if (dow === 6) return LTM_BY_ID.super_siphon;
  return LTM_BY_ID.creative;
}

export type FpsLtmBotCount = { min: number; max: number };

export function getLtmBotConfig(id: FpsLtmId): FpsLtmBotCount {
  switch (id) {
    case 'solo':         return { min: 30, max: 60 };
    case 'duo':          return { min: 20, max: 40 };
    case 'squad':        return { min: 16, max: 32 };
    case 'fifty_v_fifty':return { min: 40, max: 50 };
    case 'zone_wars':    return { min: 20, max: 30 };
    case 'tilted_only':  return { min: 20, max: 40 };
    case 'sniper_only':  return { min: 25, max: 50 };
    default:             return { min: 30, max: 60 };
  }
}
